import { DeleteOutlined, EditOutlined, PlusOutlined, RobotOutlined } from '@ant-design/icons';
import {
  ModalForm,
  PageContainer,
  ProFormDatePicker,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Calendar, Card, Drawer, Flex, Select, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { cloneElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HolidayUtil, Lunar } from 'lunar-typescript';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import SeedPlanPreview from '../components/SeedPlanPreview';
import SkillChat from '../components/SkillChat';
import { buildAgentChatEntryPath, getAgentChatEntryButtonText } from '../utils/agent-chat-entry';
import {
  createIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
  updateIpCreatorRecord,
} from '../services/ip-creator';

const RESOURCE = 'seed-plans';
const RESOURCE_NAME = '创作规划';

const isSeedPlanOutput = (value) =>
  value &&
  typeof value.title === 'string' &&
  typeof value.keyword === 'string' &&
  typeof value.description === 'string';

const getLunarText = (date) => {
  const lunar = Lunar.fromDate(date.toDate());
  const holiday = HolidayUtil.getHoliday(date.year(), date.month() + 1, date.date());
  const displayHoliday =
    holiday?.getTarget() === holiday?.getDay() ? holiday?.getName() : undefined;
  return displayHoliday || lunar.getJieQi() || lunar.getDayInChinese();
};

const getMonthLabel = (month, value) => {
  const lunar = Lunar.fromDate(new Date(value.year(), month));
  return `${month + 1}月（${lunar.getMonthInChinese()}月）`;
};

const getYearLabel = (year) => {
  const lunar = Lunar.fromDate(new Date(year + 1, 0));
  return `${year}年（${lunar.getYearInGanZhi()}${lunar.getYearShengXiao()}年）`;
};

const isPastDate = (date) => date.startOf('day').isBefore(dayjs().startOf('day'));

const buildStartPrompt = ({ date, record }) => {
  const plannedAt = date.format('YYYY-MM-DD');
  const lunar = getLunarText(date);
  if (record) {
    return `请基于以下已有创作规划编辑，并在信息足够后且我确认后输出创作规划。\n\n规划日期：${plannedAt}\n农历日期：${lunar}\n\n已有创作规划：\n规划标题：${record.title}\n规划核心：${record.keyword}\n规划描述：${record.description}`;
  }
  return `请基于以下信息开始创作规划，并在信息足够后且我确认后输出创作规划。\n\n规划日期：${plannedAt}\n农历日期：${lunar}`;
};

export const buildSkillInput = ({ date, ipKernelSnapshot, record }) => {
  const input = {
    plannedAt: date.format('YYYY-MM-DD'),
    lunarDate: getLunarText(date),
    ipKernelSnapshot:
      (typeof ipKernelSnapshot === 'string'
        ? ipKernelSnapshot
        : JSON.stringify(ipKernelSnapshot)) || '',
  };
  if (!record) {
    return input;
  }
  return {
    ...input,
    currentSeedPlan: {
      title: record.title || '',
      keyword: record.keyword || '',
      description: record.description || '',
    },
  };
};

const formatPlannedAt = (value) =>
  (dayjs.isDayjs(value) ? value : dayjs(value)).format('YYYY-MM-DD');

export const buildManualSeedPlanPayload = ({ organizationId, values }) => ({
  data: {
    organizationId,
    plannedAt: formatPlannedAt(values.plannedAt),
    title: values.title,
    keyword: values.keyword,
    description: values.description,
  },
});

const SeedPlans = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [manualCreateOpen, setManualCreateOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const navigate = useNavigate();
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const reload = () => queryClient.invalidateQueries({ queryKey: ['ip-creator', RESOURCE] });

  const { data: listResult, isLoading } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () =>
      queryIpCreatorRecords(RESOURCE, {
        current: 1,
        organizationId,
        pageSize: 200,
      }),
    queryKey: ['ip-creator', RESOURCE, organizationId],
  });

  const { data: ipKernelResult } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () =>
      queryIpCreatorRecords('ip-kernels', {
        current: 1,
        organizationId,
        pageSize: 1,
      }),
    queryKey: ['ip-creator', 'ip-kernels', organizationId, 'for-seed-plans'],
  });

  const ipKernelSnapshot = ipKernelResult?.data?.[0]?.ipKernelSnapshot || '';

  const recordsByDate = useMemo(() => {
    const records = listResult?.data || [];
    return records.reduce((map, record) => {
      const key = record.plannedAt ? dayjs(record.plannedAt).format('YYYY-MM-DD') : '';
      if (!key) {
        return map;
      }
      return {
        ...map,
        [key]: [...(map[key] || []), record],
      };
    }, {});
  }, [listResult?.data]);

  const { mutateAsync: createRecord, isPending: creating } = useMutation({
    mutationFn: (payload) => createIpCreatorRecord(RESOURCE, payload),
    onSuccess: () => {
      message.success(`${RESOURCE_NAME}已创建`);
      reload();
    },
    onError: () => message.error(`${RESOURCE_NAME}创建失败，请重试`),
  });

  const { mutateAsync: updateRecord, isPending: updating } = useMutation({
    mutationFn: (payload) => updateIpCreatorRecord(RESOURCE, payload),
    onSuccess: () => {
      message.success(`${RESOURCE_NAME}已更新`);
      reload();
    },
    onError: () => message.error(`${RESOURCE_NAME}更新失败，请重试`),
  });

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(RESOURCE, payload),
    onSuccess: () => {
      message.success(`${RESOURCE_NAME}已删除`);
      reload();
    },
    onError: () => message.error(`${RESOURCE_NAME}删除失败，请重试`),
  });

  const openCreate = (date) => {
    setDetailRecord(null);
    setEditingRecord(null);
    setEditingDate(date);
  };

  const openManualCreate = () => {
    setManualCreateOpen(true);
  };

  const openEdit = (record) => {
    setDetailRecord(null);
    setEditingRecord(record);
    setEditingDate(dayjs(record.plannedAt));
  };

  const removeRecordWithConfirm = (record) => {
    modal.confirm({
      title: `删除${RESOURCE_NAME}`,
      content: `确认删除 ${record.title || record.id}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => removeRecord({ data: { id: record.id, organizationId } }),
    });
  };

  const openAgentChatEntry = (record) => {
    navigate(resolveOrganizationPath(buildAgentChatEntryPath({ record, seedType: 'seedPlan' })));
  };

  const handleComplete = async (output) => {
    const plannedAt = editingDate.format('YYYY-MM-DD');
    try {
      if (editingRecord?.id) {
        await updateRecord({
          data: {
            id: editingRecord.id,
            organizationId,
            plannedAt,
            ...output,
          },
        });
      } else {
        await createRecord({
          data: {
            organizationId,
            plannedAt,
            ...output,
          },
        });
      }
      setEditingDate(null);
      setEditingRecord(null);
    } catch {
      // useMutation onError handles user feedback.
    }
  };

  const handleManualCreate = async (values) => {
    try {
      await createRecord(buildManualSeedPlanPayload({ organizationId, values }));
      setManualCreateOpen(false);
      return true;
    } catch {
      return false;
    }
  };

  const renderDatePickerCell = (current, info) => {
    if (info.type !== 'date') {
      return info.originNode;
    }

    return cloneElement(info.originNode, {
      children: (
        <Flex vertical align="center">
          <Typography.Text>{current.date()}</Typography.Text>
          <Typography.Text type="secondary" styles={{ root: { fontSize: 10 } }}>
            {getLunarText(current)}
          </Typography.Text>
        </Flex>
      ),
    });
  };

  const renderDateCell = (current) => {
    const key = current.format('YYYY-MM-DD');
    const dayRecords = recordsByDate[key] || [];

    return (
      <Flex vertical gap="small">
        <Typography.Text type="secondary" styles={{ root: { position: 'absolute', top: '4px' } }}>
          {getLunarText(current)}
        </Typography.Text>
        {dayRecords.map((record) => (
          <Button
            block
            key={record.id}
            type="link"
            styles={{ root: { justifyContent: 'flex-start', paddingLeft: 0, paddingRight: 0 } }}
            onClick={(event) => {
              event.stopPropagation();
              setDetailRecord(record);
            }}
          >
            <Typography.Text ellipsis={{ tooltip: record.title }}>{record.title}</Typography.Text>
          </Button>
        ))}
      </Flex>
    );
  };

  return (
    <PageContainer
      content="随时记录奇思妙想，点击日期空白处和 AI 共建创作规划"
      extra={
        <Button icon={<PlusOutlined />} type="primary" onClick={openManualCreate}>
          快速规划
        </Button>
      }
    >
      <Spin spinning={isLoading}>
        <Card variant="borderless">
          <Calendar
            cellRender={(current, info) =>
              info.type === 'date' ? renderDateCell(current) : info.originNode
            }
            disabledDate={isPastDate}
            headerRender={({ value, onChange }) => {
              const year = value.year();
              const month = value.month();
              const yearOptions = Array.from({ length: 20 }, (_, index) => year - 10 + index).map(
                (item) => ({
                  label: getYearLabel(item),
                  value: item,
                }),
              );
              const monthOptions = Array.from({ length: 12 }, (_, index) => ({
                label: getMonthLabel(index, value),
                value: index,
              }));
              return (
                <Flex justify="space-between" align="center">
                  <Space>
                    <Button size="small" onClick={() => onChange(dayjs())}>
                      本月
                    </Button>
                    <Select
                      size="small"
                      popupMatchSelectWidth={false}
                      value={year}
                      options={yearOptions}
                      onChange={(nextYear) => onChange(value.clone().year(nextYear))}
                    />
                    <Select
                      size="small"
                      popupMatchSelectWidth={false}
                      value={month}
                      options={monthOptions}
                      onChange={(nextMonth) => onChange(value.clone().month(nextMonth))}
                    />
                  </Space>
                  <Typography.Title level={5}>{value.format('YYYY年M月')}</Typography.Title>
                </Flex>
              );
            }}
            mode="month"
            onPanelChange={setSelectedDate}
            onSelect={(date, info) => {
              setSelectedDate(date);
              if (info.source === 'date' && !isPastDate(date)) {
                openCreate(date);
              }
            }}
            value={selectedDate}
          />
        </Card>
      </Spin>
      <Drawer
        destroyOnHidden
        title={`${editingRecord ? '编辑' : '创建'}${RESOURCE_NAME}`}
        open={Boolean(editingDate)}
        size={1024}
        closable={{ placement: 'end' }}
        mask={{ closable: false }}
        onClose={() => {
          setEditingDate(null);
          setEditingRecord(null);
        }}
      >
        {editingDate ? (
          <Spin spinning={creating || updating}>
            <SkillChat
              height="calc(100vh - 105px)"
              input={buildSkillInput({
                date: editingDate,
                ipKernelSnapshot,
                record: editingRecord,
              })}
              isOutputValid={isSeedPlanOutput}
              skillName="skill-seed-plan"
              startPrompt={buildStartPrompt({
                date: editingDate,
                record: editingRecord,
              })}
              strings={{
                composer: {
                  input: {
                    placeholder: '请输入你的创作规划想法',
                  },
                },
              }}
              onComplete={handleComplete}
            />
          </Spin>
        ) : null}
      </Drawer>
      <ModalForm
        title={`快速规划${RESOURCE_NAME}`}
        open={manualCreateOpen}
        width={720}
        modalProps={{
          destroyOnHidden: true,
          okButtonProps: { loading: creating },
        }}
        onOpenChange={(open) => {
          setManualCreateOpen(open);
        }}
        onFinish={handleManualCreate}
      >
        <ProFormText
          name="title"
          label="规划主题"
          rules={[{ required: true, message: '请输入规划主题' }]}
        />
        <ProFormDatePicker
          name="plannedAt"
          label="规划日期"
          fieldProps={{
            cellRender: renderDatePickerCell,
            disabledDate: isPastDate,
            format: 'YYYY-MM-DD',
          }}
          rules={[{ required: true, message: '请选择规划日期' }]}
          width="md"
        />
        <ProFormText
          name="keyword"
          label="规划核心"
          rules={[{ required: true, message: '请输入规划核心' }]}
        />
        <ProFormTextArea
          name="description"
          label="规划描述"
          fieldProps={{ autoSize: { minRows: 4, maxRows: 8 } }}
          rules={[{ required: true, message: '请输入规划描述' }]}
        />
      </ModalForm>
      <Drawer
        destroyOnHidden
        title={detailRecord?.title || `${RESOURCE_NAME}详情`}
        open={Boolean(detailRecord)}
        size={1024}
        extra={
          detailRecord ? (
            <Space>
              <Button icon={<EditOutlined />} onClick={() => openEdit(detailRecord)}>
                更新规划
              </Button>
              <Button icon={<RobotOutlined />} onClick={() => openAgentChatEntry(detailRecord)}>
                {getAgentChatEntryButtonText(detailRecord)}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={removing}
                onClick={() => removeRecordWithConfirm(detailRecord)}
              >
                删除
              </Button>
            </Space>
          ) : null
        }
        onClose={() => setDetailRecord(null)}
      >
        <SeedPlanPreview record={detailRecord} />
      </Drawer>
    </PageContainer>
  );
};

export default SeedPlans;
